import { NetworkStatus, useMutation } from '@apollo/client';
import { ErrorBoundary, ErrorRetry, SectionHeader, SuspenseLoader } from '@bechtel/client-common';
import { UPDATE_COMMODITY } from '@bechtel/client-gql';
import {
  Box,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSize,
  SideSheetSubHeader,
  Spinner,
  Text,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  CrewMember,
  CrewMemberDiscipline,
  CrewMemberList,
  CrewMemberSortByTerm,
  PersonaType,
  UpdateCommodityInput,
  UpdateCommodityPayload,
  Weld,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { Formik, FormikProps } from 'formik';
import { get, map } from 'lodash';
import { FC, Suspense, useDeferredValue, useEffect, useState } from 'react';
import { array, number, object, string } from 'yup';
import { WeldProcedure } from '~/components/atoms';
import { LocationDescription, StencilSelector, WeldedLocation } from '~/components/molecules';
import { useCrewMember, useShowMap } from '~/hooks';
import { UpdateCommodityErrorModal } from './error/internals';
import { UpdateCommodityErrorExt } from './error/types';
import { FooterButtonStyled } from './styled';
import { EditWeldDetailsFormikState } from './types';

interface EditWeldDetailsProps {
  closeSidesheet: () => void;
  weld: Weld;
  refetchQueries?: string[];
}

const editWeldDetailValidationSchema = object().shape({
  weldProcedure: array()
    .required()
    .min(1, t`Select at least 1 weld procedure`),
  stencils: array()
    .required()
    .min(1, t`Select at least 1 stencil`),
  weldedLocation: object().when('showMap', {
    is: true,
    then: object()
      .shape({
        latitude: number(),
        longitude: number(),
      })
      .required(t`Location is mandatory`),
    otherwise: object().nullable(),
  }),
  locationDesc: string().max(2000, t`Too Long!`),
});
export const EditWeldDetails: FC<EditWeldDetailsProps> = ({ closeSidesheet, weld, refetchQueries }) => {
  const { applicableWeldProcedures, commodityAssignees, weldedWithProcedure, location, locationDescription, tag, id } =
    weld;

  const { showMap } = useShowMap();
  const dispatchToast = useToastsDispatch();
  const [updateCommodityError, setUpdateCommodityError] = useState<UpdateCommodityErrorExt | null>();
  const [stencilSearchQuery, setStencilSearchQuery] = useState('');
  const debouncedSearchQuery = useDeferredValue(stencilSearchQuery);

  const [
    getWelderList,
    {
      data: crewMemberData,
      error: crewMemberError,
      loading: crewMemberLoading,
      refetch: crewMemberRefetch,
      networkStatus: crewMemberNetworkStatus,
    },
  ] = useCrewMember();
  const crewMemberList = get(crewMemberData, 'crewMembers.items', []) as CrewMemberList['items'];
  const selectedStencilsList: CrewMember[] | undefined = commodityAssignees
    ?.filter((commodityAssignee) => commodityAssignee.personaType === PersonaType.CRAFT)
    .map((commodityAssignee) => {
      return commodityAssignee.assignee;
    }) as CrewMember[] | undefined;

  const initialFormikValues: EditWeldDetailsFormikState = {
    showMap: showMap,
    weldProcedure: weldedWithProcedure ? weldedWithProcedure : null,
    stencils: selectedStencilsList && selectedStencilsList?.length > 0 ? selectedStencilsList : [],
    weldedLocation:
      showMap && !!location?.latitude && !!location?.longitude
        ? {
            latitude: location?.latitude,
            longitude: location?.longitude,
          }
        : null,
    locationDesc: locationDescription || '',
  };

  const [updateCommodity, { loading: mutationLoading }] = useMutation<
    { updateCommodityPayload: UpdateCommodityPayload },
    { input: UpdateCommodityInput }
  >(UPDATE_COMMODITY, {
    onCompleted: (data) => {
      const error = get(data, 'updateCommodity.error');
      if (error) {
        setUpdateCommodityError(error);
      } else {
        setUpdateCommodityError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: t`Weld details updated successfully`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
        closeSidesheet();
      }
    },
    onError: (error) => {
      setUpdateCommodityError(error);
    },
    refetchQueries: refetchQueries,
  });

  const closeModal = () => {
    setUpdateCommodityError(null);
  };

  useEffect(() => {
    if (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 2) return;
    getWelderList({
      variables: {
        input: {
          discipline: CrewMemberDiscipline.WELDING,
          sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
          searchKeyword: debouncedSearchQuery,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [getWelderList, debouncedSearchQuery]);

  return (
    <>
      <Formik
        initialValues={initialFormikValues}
        validationSchema={editWeldDetailValidationSchema}
        enableReinitialize={true}
        onSubmit={async (values) => {
          const UpdateCommodityInput: UpdateCommodityInput = {
            commodityId: id,
            weldProcedureIds: map(values.weldProcedure, (item: any) => item?.id),
            stencilIds: map(values.stencils, (item) => item.id),
            location: values?.weldedLocation
              ? {
                  latitude: values?.weldedLocation?.latitude,
                  longitude: values?.weldedLocation?.longitude,
                }
              : undefined,
            locationDescription: values.locationDesc,
          };
          await updateCommodity({
            variables: { input: UpdateCommodityInput },
          });
        }}
      >
        {(props: FormikProps<EditWeldDetailsFormikState>) => {
          const { handleSubmit, dirty, isValid, values } = props;
          return (
            <SideSheet size={SideSheetSize.DEFAULT} onDismiss={closeSidesheet} visible data-testid="edit-weld-details">
              <SideSheetHeader title={t`Update Weld Details`}>
                <SideSheetClose onClick={closeSidesheet}></SideSheetClose>
              </SideSheetHeader>
              <SideSheetSubHeader>
                <Box
                  flexDirection={{
                    sm: 'column',
                  }}
                >
                  <Box mb={{ sm: '200' }}>
                    <Text color="grey.1000" size="400" weight="600">
                      {tag}
                    </Text>
                  </Box>
                  <Text color="grey.500" size="300">
                    <Trans>Weld</Trans>
                  </Text>
                </Box>
              </SideSheetSubHeader>
              <SideSheetBody data-testid="edit-weld-details-body">
                <SideSheetContentCol>
                  <Box flexDirection={{ sm: 'column' }} data-testid="edit-weld-content">
                    <SectionHeader title={t`Weld Procedure(s)`} />
                    <WeldProcedure items={applicableWeldProcedures} fieldName="weldProcedure" />
                    <SectionHeader title={t`Stencil(s)`} />
                    <Box px={{ sm: '450' }} py={{ sm: '500' }} data-testid="weld-stencils">
                      {!crewMemberLoading && crewMemberError && (
                        <div data-testid="welder-error">
                          <ErrorRetry refetch={crewMemberRefetch}>
                            <Trans>There was a problem loading Stencils</Trans>
                          </ErrorRetry>
                        </div>
                      )}
                      {!crewMemberError && (
                        <StencilSelector
                          fieldName="stencils"
                          items={crewMemberList}
                          enableClientSearch={false}
                          onSearch={(query) => {
                            setStencilSearchQuery(query);
                          }}
                          loading={crewMemberLoading || crewMemberNetworkStatus === NetworkStatus.refetch}
                        />
                      )}
                    </Box>
                    {values?.showMap && (
                      <>
                        <SectionHeader title={t`Weld Location`} />
                        <Box flexDirection={{ sm: 'column' }} px={{ sm: '450' }} py={{ sm: '500' }}>
                          <WeldedLocation name="weldedLocation" />
                        </Box>
                      </>
                    )}

                    <SectionHeader title={t`Weld Location Description`} />
                    <Box px={{ sm: '450' }} py={{ sm: '500' }} data-testid="weld-location-description">
                      <LocationDescription name="locationDesc" />
                    </Box>
                  </Box>
                </SideSheetContentCol>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  <Box mr={{ sm: '400' }}>
                    {!mutationLoading && (
                      <FooterButtonStyled data-testid="weld-cancel" onClick={closeSidesheet} disabled={mutationLoading}>
                        <Trans>Cancel</Trans>
                      </FooterButtonStyled>
                    )}
                  </Box>
                  <Box>
                    <FooterButtonStyled
                      data-testid="weld-update"
                      onClick={() => handleSubmit()}
                      disabled={!isValid || !dirty || mutationLoading}
                    >
                      {mutationLoading && <Spinner />}
                      {!mutationLoading && <Trans>Update</Trans>}
                    </FooterButtonStyled>
                  </Box>
                </Box>
              </SideSheetFooter>
            </SideSheet>
          );
        }}
      </Formik>
      {updateCommodityError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <UpdateCommodityErrorModal error={updateCommodityError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
